import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState, useEffect } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Divider,
  VStack,
  FormControl,
  FormErrorMessage,
  FormHelperText,
} from '@chakra-ui/react';
import { MdArticle } from 'react-icons/md';

import { createNarrativeSubQuest } from 'store/modules/subquest/subquest-action';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { ReducerStatus } from 'utils/constants';
import SubQuestForm from 'components/subQuest/SubQuestForm';
import ResultModal from 'components/common/modals/ResultModal';
import { arrayOfString } from 'utils/jsonParser';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageCompressor from 'quill-image-compress';

import {
  useQuillConfig,
  calculateContentSize,
} from 'utils/react-quill/quillConfig';

import RichTextSizeIndicator from 'utils/react-quill/RichTextSizeIndicator';

ReactQuill.Quill.register('modules/imageCompress', imageCompressor);

function CreateNarrativeMd({ quest }) {
  const { modules, formats, style } = useQuillConfig();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });
  const [formInputs, setFormInputs] = useState({
    content: '',
  });
  const [contentSize, setContentSize] = useState(0);

  const createNarrative = useSelector(
    (state) => state.subquest.createNarrative
  );

  const formikForm = useRef();
  const quillRef = useRef(null);
  const formValidation = Yup.object({
    content: Yup.string()
      .test('not-empty', 'Content is required', (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
      })
      .test('file-size', 'Content size exceeds 5MB limit', (value) => {
        if (!value) return true;
        const calculatedSize = calculateContentSize(value);
        return calculatedSize <= 5;
      })
      .required('Content is required'),
  });

  useEffect(() => {
    if (quest.ownerGroups) {
      setSubQuestFormInputs((prev) => ({
        ...prev,
        groups: arrayOfString(quest.ownerGroups),
      }));
    }
  }, [quest.ownerGroups]);

  useEffect(() => {
    if (isFormSubmit && isSubQuestFormValid) {
      formikForm?.current?.submitForm();
      setIsFormSubmit(false);
    }
  }, [isFormSubmit, isSubQuestFormValid]);

  const handleTriggerSubmit = () => {
    formikForm.current?.setFieldTouched('content', true, false);
    formikForm.current?.validateField('content');

    setTriggerSubQuestForm((trigger) => trigger + 1);
    setIsFormSubmit(true);
  };

  const handleSubmitForm = (values) => {
    if (isSubQuestFormValid === false) return;

    setFormInputs({ ...values });

    const narrativeData = {
      Name: subQuestFormInputs.name,
      Objective: subQuestFormInputs.objective,
      Code: subQuestFormInputs.code,
      IsPublished:
        subQuestFormInputs.isPublished === 'true' ||
        subQuestFormInputs.isPublished === true,
      Tags: subQuestFormInputs.tags || [],
      OwnerGroups: subQuestFormInputs.groups || [],
      Content: values.content,
    };

    dispatch(
      createNarrativeSubQuest({
        id: quest.id,
        formData: narrativeData,
      })
    );
  };

  const handleResult = () => {
    if (createNarrative.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        content: '',
      });

      setSubQuestFormInputs({
        name: '',
        objective: '',
        code: '',
        isPublished: '',
        tags: [],
        groups: [],
      });

      setTriggerSubQuestForm(-1);
      onClose();
    }
    dispatch(subquestActions.resetCreateNarrative());
  };

  return (
    <Box>
      {createNarrative.status === ReducerStatus.SUCCEEDED ||
      createNarrative.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={isOpen}
          onSubmit={handleResult}
          isError={createNarrative.status === ReducerStatus.FAILED}
          title={
            createNarrative.status === ReducerStatus.SUCCEEDED
              ? 'Narrative Sub-Quest Created'
              : null
          }
          message={
            createNarrative.status === ReducerStatus.SUCCEEDED
              ? 'The narrative sub-quest has been created successfully.'
              : createNarrative.error?.data?.title || 'Server Error'
          }
          errorObject={createNarrative.error?.data?.errors}
        />
      ) : (
        <>
          <Button
            onClick={onOpen}
            leftIcon={<MdArticle />}
            w="100%"
            colorScheme="red"
            size="lg"
          >
            Narrative
          </Button>
          <Modal
            size="2xl"
            closeOnOverlayClick={
              createNarrative.status !== ReducerStatus.LOADING
            }
            isOpen={isOpen}
            onClose={onClose}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Create Narrative Sub-Quest</ModalHeader>
              <ModalCloseButton
                disabled={createNarrative.status === ReducerStatus.LOADING}
              />
              <ModalBody>
                <SubQuestForm
                  questName={quest.name}
                  groups={quest.ownerGroups}
                  subQuestFormInputs={subQuestFormInputs}
                  triggerSubQuestForm={triggerSubQuestForm}
                  setSubQuestFormInputs={setSubQuestFormInputs}
                  setIsSubQuestFormValid={setIsSubQuestFormValid}
                />
              </ModalBody>
              <Formik
                enableReinitialize
                innerRef={formikForm}
                initialValues={formInputs}
                onSubmit={(values) => {
                  handleSubmitForm(values);
                }}
                validationSchema={formValidation}
              >
                {({ errors, touched }) => (
                  <Box>
                    <Form>
                      <ModalBody>
                        <Divider mb={4} />
                        <VStack spacing={4} align="stretch">
                          <FormControl
                            isInvalid={errors.content && touched.content}
                          >
                            <Field name="content">
                              {({ field, form }) => (
                                <Box position="relative" sx={style}>
                                  <ReactQuill
                                    ref={quillRef}
                                    value={field.value}
                                    onChange={(content) => {
                                      const newContentSize =
                                        calculateContentSize(content);
                                      setContentSize(newContentSize);
                                      form.setFieldValue(field.name, content);
                                    }}
                                    onBlur={() => {
                                      form.setFieldTouched(field.name, true);
                                    }}
                                    placeholder="Enter narrative content"
                                    style={{
                                      border:
                                        form.errors.content &&
                                        form.touched.content
                                          ? '2px solid #e53e3e'
                                          : '1px solid #e2e8f0',
                                      borderRadius: '6px',
                                    }}
                                    theme="snow"
                                    modules={modules}
                                    formats={formats}
                                  />
                                  <RichTextSizeIndicator size={contentSize} />
                                </Box>
                              )}
                            </Field>
                            <FormErrorMessage>
                              {errors.content}
                            </FormErrorMessage>
                            <FormHelperText>
                              Provide a detailed narrative for the sub-quest
                              using the rich text editor.
                            </FormHelperText>
                          </FormControl>
                        </VStack>
                      </ModalBody>

                      <ModalFooter>
                        <Button
                          variant="outline"
                          colorScheme="blue"
                          mr={3}
                          onClick={onClose}
                          isLoading={
                            createNarrative.status === ReducerStatus.LOADING
                          }
                        >
                          Close
                        </Button>
                        <Button
                          isLoading={
                            createNarrative.status === ReducerStatus.LOADING
                          }
                          onClick={handleTriggerSubmit}
                          colorScheme="red"
                          px={10}
                        >
                          Submit
                        </Button>
                      </ModalFooter>
                    </Form>
                  </Box>
                )}
              </Formik>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
}

CreateNarrativeMd.propTypes = {
  quest: PropTypes.object.isRequired,
};

export default CreateNarrativeMd;
